import React, { useState } from 'react';
import { Company, GpsDevice, GpsDeviceCompany } from '../../Types';
import { COLORS } from '../../Styles/Colors';
import useGetData from '../../Hooks/useGetData';
import useGetCustomData from '../../Hooks/useGetCustomData';
import {
  Checkbox,
  ListItemText,
  MenuItem,
  Select,
  Box,
} from '@mui/material';
import {
  Form,
  FormSection,
  SectionTitle,
  SectionDescription,
  FormSelect,
  FormField,
  selectFocusSx,
  checkboxSx,
} from '../../Components/Form';
import { CompanySelect } from '../../Components/Select/CompanySelect';

interface Props {
  devices: GpsDevice[];
  onSubmit: (data: any) => void;
  onCancel: () => void;
}

const GpsForm: React.FC<Props> = ({ devices, onSubmit, onCancel }) => {
  const [companyDevices] = useGetData<GpsDeviceCompany>('/api/gps/devices/company');
  const [companies] = useGetData<Company>('/api/company/user');
  useGetCustomData('/api/gps/assignment');

  const [selectedDeviceIds, setSelectedDeviceIds] = useState<any[]>([]);
  const [CID, setCID] = useState('');
  const [action, setAction] = useState<'assign' | 'reassign' | 'remove'>('assign');

  const assignedDeviceIds = new Set(companyDevices.map((item: any) => item.device_id));

  const updatedDevices = devices?.map((device: any) => ({
    ...device,
    assigned: assignedDeviceIds.has(device.device_id) ? 1 : 0,
  }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    let formData;

    switch (action) {
      case 'assign':
      case 'reassign':
        formData = { action, deviceIds: selectedDeviceIds, companyId: CID };
        break;
      case 'remove':
        formData = { action: 'remove', deviceIds: selectedDeviceIds };
        break;
      default:
        formData = { action: 'assign', deviceIds: selectedDeviceIds, companyId: CID };
    }

    onSubmit(formData);
  };

  const getDeviceAssignedCompany = (deviceId: string) => {
    const assignment = companyDevices.find((d) => d.device_id === deviceId);
    if (assignment) {
      const company = companies.find((c) => String(c.CID) === String(assignment.company_id));
      return company?.name || 'Unknown Company';
    }
    return null;
  };

  const isDeviceDisabled = (device: any) => {
    const isAssigned = device.assigned === 1;

    switch (action) {
      case 'assign':
        return isAssigned;
      case 'reassign':
      case 'remove':
        return !isAssigned;
      default:
        return false;
    }
  };

  const handleActionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setAction(e.target.value as 'assign' | 'reassign' | 'remove');
    setSelectedDeviceIds([]);
  };

  const getButtonText = () => {
    switch (action) {
      case 'assign': return 'Assign Devices';
      case 'reassign': return 'Reassign Devices';
      case 'remove': return 'Remove Devices';
      default: return 'Assign Devices';
    }
  };

  const isCompanyRequired = action !== 'remove';

  const deviceLabel =
    action === 'assign' ? 'Available Devices' :
    action === 'reassign' ? 'Devices to Reassign' :
    'Assigned Devices to Remove';

  return (
    <Form
      embedded
      hideTitle
      onSubmit={handleSubmit}
      title="GPS Device Assignment"
      subtitle="Assign, reassign, or remove GPS devices from companies."
      submitLabel={getButtonText()}
      onCancel={onCancel}
      disableSubmit={selectedDeviceIds.length === 0 || (isCompanyRequired && !CID)}
    >
      <FormSection>
        <SectionTitle>Action</SectionTitle>
        <SectionDescription>Choose what operation to perform on GPS devices.</SectionDescription>
        <FormSelect
          id="action"
          label="Action"
          value={action}
          onChange={handleActionChange}
          required
          options={[
            { value: 'assign', label: 'Assign Devices' },
            { value: 'reassign', label: 'Reassign Devices' },
            { value: 'remove', label: 'Remove Devices' },
          ]}
        />
      </FormSection>

      {isCompanyRequired && (
        <FormSection>
          <SectionTitle>Company</SectionTitle>
          <SectionDescription>Select the company for this assignment.</SectionDescription>
          <CompanySelect
            id="company"
            required
            fullWidth
            companies={companies}
            selectedCompany={CID}
            defaultName="Select a Company"
            onChange={(newCompany: string | null) =>
              setCID(newCompany ? String(newCompany) : '')
            }
            sx={{ minWidth: '100%', width: '100%' }}
          />
        </FormSection>
      )}

      <FormSection>
        <SectionTitle>Devices</SectionTitle>
        <SectionDescription>
          {action === 'assign' && 'Select unassigned devices to assign to the company.'}
          {action === 'reassign' && 'Select assigned devices to move to another company.'}
          {action === 'remove' && 'Select assigned devices to remove from their company.'}
        </SectionDescription>
        <FormField label={deviceLabel} required>
          {!updatedDevices || updatedDevices.length === 0 ? (
            <Box
              sx={{
                p: 2,
                textAlign: 'center',
                color: COLORS.gray,
                border: `1px solid ${COLORS.gray}`,
                borderRadius: '6px',
              }}
            >
              No devices available
            </Box>
          ) : (
            <Select
              multiple
              fullWidth
              size="small"
              value={selectedDeviceIds}
              onChange={(e) => setSelectedDeviceIds(e.target.value as string[])}
              renderValue={(selected) =>
                updatedDevices
                  .filter((d: any) => selected.includes(d.device_id))
                  .map((d: any) => d.name)
                  .join(', ')
              }
              sx={selectFocusSx}
            >
              {updatedDevices.map((device: any) => {
                const assignedCompany = getDeviceAssignedCompany(device.device_id);
                const isDisabled = isDeviceDisabled(device);

                return (
                  <MenuItem
                    key={device.device_id}
                    value={device.device_id}
                    disabled={isDisabled}
                  >
                    <Checkbox
                      checked={selectedDeviceIds.includes(device.device_id)}
                      disabled={isDisabled}
                      size="small"
                      sx={checkboxSx}
                    />
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          {device.name} ({device.device_id})
                          {device.asset_name && (
                            <Box component="span" sx={{ backgroundColor: COLORS.teal, color: COLORS.white, fontSize: '0.7em', px: 1, borderRadius: '4px' }}>
                              Asset: {device.asset_name}
                            </Box>
                          )}
                          {device.fleet_name && (
                            <Box component="span" sx={{ backgroundColor: COLORS.darkTeal, color: COLORS.white, fontSize: '0.7em', px: 1, borderRadius: '4px' }}>
                              Fleet: {device.fleet_name}
                            </Box>
                          )}
                          {device.assigned === 1 && (
                            <Box component="span" sx={{ backgroundColor: COLORS.orange, color: COLORS.white, fontSize: '0.7em', px: 1, borderRadius: '4px' }}>
                              {assignedCompany || 'Assigned'}
                            </Box>
                          )}
                        </Box>
                      }
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        sx: { opacity: isDisabled ? 0.5 : 1 },
                      }}
                    />
                  </MenuItem>
                );
              })}
            </Select>
          )}
        </FormField>
      </FormSection>
    </Form>
  );
};

export default GpsForm;
